/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Chip, IconButton, LinearProgress, Paper, Stack, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { campaignsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabelPlural, formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'

export const STATUS = { DRAFT: ['Draft', 'default'], SENDING: ['Sending', 'primary'], PAUSED: ['Paused', 'warning'], SENT: ['Sent', 'success'], CANCELLED: ['Cancelled', 'default'] }

/** Campaigns (admins): one email to many records, and the do-not-email list. */
const CampaignsPage = () => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const { data = [], isLoading, error } = useQuery({ queryKey: [tenantId, 'campaigns'], queryFn: campaignsAPI.list, refetchInterval: 15_000 })
  return (
    <Box sx={{ maxWidth: 960 }}>
      <PageHeader title="Campaigns" subtitle="One email to many contacts, each filled in for its record, with an unsubscribe link."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/campaigns/new')}>New campaign</Button>} />
      {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
      {isLoading ? <Loading /> : data.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}><Typography color="text.secondary">No campaigns yet.</Typography></Paper>
      ) : (
        <Paper variant="outlined" sx={{ mb: 3 }}>
          {data.map((c, i) => {
            const done = c.sent + c.failed + c.skipped
            return (
              <Box key={c.id} onClick={() => navigate(`/campaigns/${c.id}`)} sx={{ px: 2, py: 1.5, cursor: 'pointer', borderTop: i ? 1 : 0, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>{c.name}</Typography>
                  <Chip size="small" label={STATUS[c.status][0]} color={STATUS[c.status][1]} variant="outlined" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {entityLabelPlural(c.entityType)} · {c.status === 'DRAFT' ? `created ${formatRelativeTime(c.createdOn)}` : `${c.sent} sent of ${c.total - c.skipped}${c.failed ? ` · ${c.failed} failed` : ''}`}
                </Typography>
                {c.status === 'SENDING' && c.total > 0 && <LinearProgress variant="determinate" value={(done / c.total) * 100} sx={{ mt: 0.75 }} />}
              </Box>
            )
          })}
        </Paper>
      )}
      <Suppressions />
    </Box>
  )
}

/** Addresses that never get campaigns: unsubscribed, or added here. */
const Suppressions = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [email, setEmail] = useState('')
  const { data = [] } = useQuery({ queryKey: [tenantId, 'campaigns', 'suppressions'], queryFn: campaignsAPI.suppressions })
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'campaigns', 'suppressions'] })
  const add = useMutation({ mutationFn: () => campaignsAPI.suppress(email.trim()), onSuccess: () => { setEmail(''); refresh() }, onError: (e) => notify(getErrorMessage(e), 'error') })
  const allow = useMutation({ mutationFn: campaignsAPI.allow, onSuccess: refresh })
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2">Do-not-email list</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        People who unsubscribed, and addresses you add. Campaigns skip them; one-to-one emails from a record still go.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} component="form" onSubmit={(e) => { e.preventDefault(); if (email.trim()) add.mutate() }}>
        <TextField size="small" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ flexGrow: 1, maxWidth: 360 }} />
        <Button type="submit" disabled={!email.trim() || add.isPending}>Add</Button>
      </Stack>
      {data.length === 0 ? <Typography variant="caption" color="text.secondary">Nobody yet.</Typography> : data.map((s) => (
        <Stack key={s.email} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>{s.email}</Typography>
          <Typography variant="caption" color="text.secondary">{s.reason === 'UNSUBSCRIBED' ? 'unsubscribed' : 'added'} {formatRelativeTime(s.createdOn)}</Typography>
          <IconButton size="small" aria-label={`Allow ${s.email}`} onClick={() => allow.mutate(s.email)}><DeleteOutlineIcon fontSize="small" /></IconButton>
        </Stack>
      ))}
    </Paper>
  )
}

export default CampaignsPage
